import * as storage from '@/utils/storage'

const BEFORE_YOU_START_POPUP_KEY = 'beforeYouStartPopup'

interface BeforeYouStartPopupState {
  [userSponsorKey: string]: boolean // true = popup has been shown
}

/**
 * Generate a unique key for user+sponsor combination
 */
function getUserSponsorKey(userId: string, sponsorId: string): string {
  return `${userId}:${sponsorId}`
}

/**
 * Check if the "Before you Start" popup has been shown for this user/sponsor combination
 */
export function hasSeenBeforeYouStartPopup(userId: string, sponsorId: string): boolean {
  const state = storage.load<BeforeYouStartPopupState>(BEFORE_YOU_START_POPUP_KEY) || {}
  const key = getUserSponsorKey(userId, sponsorId)
  return state[key] === true
}

/**
 * Mark the "Before you Start" popup as shown for this user/sponsor combination
 */
export function markBeforeYouStartPopupAsSeen(userId: string, sponsorId: string): boolean {
  const state = storage.load<BeforeYouStartPopupState>(BEFORE_YOU_START_POPUP_KEY) || {}
  const key = getUserSponsorKey(userId, sponsorId)

  state[key] = true

  return storage.save(BEFORE_YOU_START_POPUP_KEY, state)
}

/**
 * Check if the popup should be shown based on the current state:
 * - User must be sponsor admin
 * - User must be in setup mode
 * - Popup must not have been seen yet for this user/sponsor combination
 */
export function shouldShowBeforeYouStartPopup(
  isSponsorAdmin: boolean,
  isInSetup: boolean,
  userId: string | undefined,
  sponsorId: string | undefined,
): boolean {
  if (!isSponsorAdmin || !isInSetup || !userId || !sponsorId) {
    return false
  }

  return !hasSeenBeforeYouStartPopup(userId, sponsorId)
}

/**
 * Reset the "Before you Start" popup state for a specific user/sponsor combination
 * Useful for development and testing
 */
export function resetBeforeYouStartPopupForUser(userId: string, sponsorId: string): boolean {
  const state = storage.load<BeforeYouStartPopupState>(BEFORE_YOU_START_POPUP_KEY) || {}
  const key = getUserSponsorKey(userId, sponsorId)

  delete state[key]

  return storage.save(BEFORE_YOU_START_POPUP_KEY, state)
}

/**
 * Reset all "Before you Start" popup state (clear all users/sponsors)
 * Useful for development and testing
 */
export function resetAllBeforeYouStartPopupState(): boolean {
  return storage.save(BEFORE_YOU_START_POPUP_KEY, {})
}
